import React, { useState } from "react";
import { getBrandName } from '../getBrandName';

export const useTermsLogic = () => {
  const [active, setActive] = useState("definitions");
  const {mail, brandName, normalizedBrand,phone } = getBrandName();

  const sections = [
    { id: "definitions", title: "Definitions" },
    { id: "eligibility", title: "Eligibility & User Obligations" },
    { id: "usage", title: "Permitted & Prohibited Use" },
    { id: "intellectual", title: "Intellectual Property" },
    { id: "api", title: "API Access and Usage" },
    { id: "availability", title: "Service Availability & Disclaimers" },
    { id: "liability", title: "Limitation of Liability" },
    { id: "termination", title: "Suspension and Termination" },
    { id: "privacy", title: "Privacy & Data Handling" },
    { id: "law", title: "Governing Law & Jurisdiction" },
    { id: "modifications", title: "Modifications to the Terms" },
    { id: "subscription", title: "Subscription, Billing & Payment Terms (if applicable)" },
    { id: "thirdparty", title: "Third-Party Services & Integrations" },
    { id: "userdata", title: "User-Submitted Content & Data Responsibility" },
    { id: "feedback", title: "Feedback & Suggestions" },
    { id: "force", title: "Force Majeure" },
    { id: "severability", title: "Severability" },
    { id: "entire", title: "Entire Agreement" },
    { id: "grievance", title: "Grievance Redressal Mechanism" },
    { id: "contact", title: "Contact Information" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generateContent = (id) => {
    const contentMap = {
      definitions: `
• "${brandName}," "we," "us," or "our" refers to the platform and the entity operating it.

• "User," "you," or "your" refers to any person or entity using our website or services.

• "Platform" includes our website, web applications, APIs, dashboards, content, analytics, and any associated tools.

• "Services" refers to all services provided by ${brandName}, including data analysis, reporting, user account management, and API access.`,

      eligibility: `2.1 Eligibility
• You are at least 18 years old and legally capable of entering into a binding agreement under applicable laws.
• You are using the platform for lawful purposes related to your business or e-commerce activities.

2.2 Registration & Account Security
• You may be required to create an account to access certain features of our platform.
• You are responsible for providing accurate and complete information during registration.
• You agree to keep your login credentials confidential and notify us immediately of any unauthorized use or security breach.`,

      usage: `3.1 Permitted Use
• For legitimate business purposes (e.g., e-commerce sales tracking, performance analysis)
• In compliance with all applicable laws and regulations
• In a manner that does not interfere with the security, integrity, or performance of our systems

3.2 Prohibited Use
• Misuse, hack, reverse-engineer, or disrupt any part of the platform
• Upload or transmit any malicious code or data
• Attempt unauthorized access to other users' data or accounts
• Use our services to engage in fraud, spamming, scraping, or abusive behavior
• Resell or commercially exploit our platform without permission`,

      intellectual: `All content and intellectual property rights in the platform are owned by ${brandName} or its licensors. This includes:
• Logos, branding, user interface designs
• Content, analytics reports, and insights
• Algorithms, and APIs

You are not permitted to copy, distribute, reproduce, or create derivative works from our content without prior written consent.`,

      api: `If you are granted access to ${brandName}’s APIs:
• You may use them only in accordance with our API documentation and rate limits
• Abuse of API access (e.g., bypassing authentication, causing service overload) may result in immediate suspension or termination
• We reserve the right to modify, restrict, or discontinue API services at any time, with or without notice`,

      availability: `We strive to maintain high availability and performance. However:
• ${brandName} is provided "as is" and "as available."
• We do not guarantee the platform will be uninterrupted, error-free, or completely secure
• We disclaim all warranties, express or implied, including those of merchantability, accuracy, and fitness for a particular purpose

*Note: Use of the platform is at your own risk.`,

      liability: `To the maximum extent permitted by law:
• ${brandName} shall not be liable for any indirect, incidental, special, or consequential damages, including loss of profits, data, or goodwill arising from your use of or inability to use the platform
• Our total liability for any claims under these Terms shall not exceed the amount paid (if any) by you for using the services during the prior 3 months`,

      termination: `We reserve the right to suspend, restrict, or terminate your access to the platform if:
• You violate these Terms
• Your account poses a security threat or causes harm to others
• Required by legal or regulatory obligations

Upon termination:
• Your rights to access and use our services will cease immediately.
• You may lose access to your account data unless otherwise provided by law or agreement.`,

      privacy: `Your use of ${brandName} is also governed by our Privacy Policy, which outlines how we collect, use, and safeguard your information.
By using the platform, you consent to our use of your data in accordance with the Privacy Policy.`,

      law: `These Terms shall be governed by and construed in accordance with the laws of India.
Any disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in New Delhi, India.`,

      modifications: `We may update or revise these Terms periodically. If changes are made:
• We will publish the updated version on our website
• The updated Terms will become effective immediately unless otherwise stated
• Continued use of the platform after updates constitutes your acceptance of the new Terms`,

      subscription: `If ${brandName} offers paid subscriptions or features, the following terms apply:
• Billing: Subscription fees are billed on a recurring basis (monthly, quarterly, annually)
• Payment Method: You must provide a valid payment method. Failed payments may result in suspension.
• Auto-renewal: Subscriptions may auto-renew unless canceled before the next billing cycle.
• Refunds: Except as required by law, payments are non-refundable.`,

      thirdparty: `Our platform may integrate with third-party services (e.g., Amazon, Shopify, Google Analytics).
• We do not control or take responsibility for those services.
• You are responsible for reviewing and complying with third-party terms and privacy policies.
• Integrations may be subject to additional terms, usage limits, or costs.`,

      userdata: `${brandName} may allow you to upload data (e.g., reports, catalogs).
• You confirm you have rights to use and share such data.
• You grant us a limited license to use it to provide services.
• ${brandName} is not liable for content or accuracy of uploaded data.`,

      feedback: `We welcome suggestions and feedback.
• All feedback is considered non-confidential and non-proprietary.
• We may use it without obligation or compensation.`,

      force: `${brandName} is not responsible for delays or failure due to:
• Acts of God, natural disasters
• Internet failures or cyberattacks
• Government restrictions or technical outages`,

      severability: `If any part of these Terms is deemed invalid, the rest will remain enforceable.`,

      entire: `These Terms, along with our Privacy Policy and other referenced policies, form the complete agreement between you and ${brandName}.`,

      grievance: `You may contact our Grievance Officer regarding data handling or policy violations, as per Indian law.`,

    };

    const lines = contentMap[id]?.split("\n") || [];
    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (/^\d+\.\d+/.test(line)) {
        flushList();
        contentElements.push(
          <h6 key={`subheading-${index}`} style={{ marginTop: "1rem" }}>
            {line}
          </h6>
        );
      } else if (/^•/.test(line)) {
        items.push(line.replace(/^•\s*/, ""));
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
